/**
 * Query Builder export configuration
 *
 * USAGE:
 * ```typescript
 * const config = useMemo(() =>
 *   createQueryExportConfig(savedQuery),
 *   [savedQuery]
 * );
 *
 * <UniversalExportModal config={config} />
 * ```
 *
 * DESIGN:
 * - Derives entity type from query's root node label
 * - Passes query's GraphQuery directly to /export/{entityType}
 * - No additional filters (the query IS the filter)
 * - Supports: Asset, Risk, Seed, Technology queries
 * - Returns null for unsupported entity types (Port, ADObject, etc.)
 */

import type {
  ExportableEntity,
  ExportColumnConfig,
  UniversalExportConfig,
} from '@/components/export/universal/types';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

import { AD_COLUMNS } from './ad.export.config';
import { ASSET_COLUMNS } from './asset.export.config';
import { CLOUD_COLUMNS } from './cloud.export.config';
import { PORT_COLUMNS } from './port.export.config';
import { SEED_COLUMNS } from './seed.export.config';
import { TECHNOLOGY_COLUMNS } from './technology.export.config';
import { VULNERABILITY_COLUMNS } from './vulnerability.export.config';
import { WEBPAGE_COLUMNS } from './webpage.export.config';

/**
 * Map root node label to backend export entity type
 * Only these entity types are supported for export
 */
const LABEL_TO_ENTITY: Record<
  string,
  | 'asset'
  | 'risk'
  | 'seed'
  | 'technology'
  | 'webpage'
  | 'port'
  | 'adobject'
  | 'awsresource'
  | 'gcpresource'
  | 'azureresource'
  | 'cloudresource'
  | 'vulnerability'
> = {
  // Existing
  asset: 'asset',
  risk: 'risk',
  seed: 'seed',
  technology: 'technology',
  // New: Web
  webpage: 'webpage',
  webapplication: 'webpage',
  // New: Network
  port: 'port',
  // New: AD (all 15 subtypes map to 'adobject')
  adobject: 'adobject',
  aduser: 'adobject',
  adgroup: 'adobject',
  adcomputer: 'adobject',
  addomain: 'adobject',
  adgpo: 'adobject',
  adou: 'adobject',
  adcontainer: 'adobject',
  adlocalgroup: 'adobject',
  adlocaluser: 'adobject',
  adaiaca: 'adobject',
  adrootca: 'adobject',
  adenterpriseca: 'adobject',
  adntauthstore: 'adobject',
  adcerttemplate: 'adobject',
  adissuancepolicy: 'adobject',
  // New: Cloud
  awsresource: 'awsresource',
  gcpresource: 'gcpresource',
  azureresource: 'azureresource',
  cloudresource: 'cloudresource',
  // New: Vulnerability
  vulnerability: 'vulnerability',
};

/**
 * Get API entity type from query node labels
 *
 * @param labels - Node labels from query (e.g., ['Asset'], ['Risk'])
 * @returns API entity type or undefined if not supported
 *
 * @example
 * getApiEntityType(['Asset']) → 'asset' (supported)
 * getApiEntityType(['Port', 'Asset']) → 'asset' (Port ignored, Asset found)
 * getApiEntityType(['Asset', 'Risk']) → undefined (ambiguous mixed types)
 * getApiEntityType(['Port', 'ADObject']) → undefined (no supported type)
 * getApiEntityType(['Asset', 'Asset']) → 'asset' (deduped, same type)
 * getApiEntityType(['ADUser']) → 'adobject' (AD subtype mapped to adobject)
 */
export function getApiEntityType(
  labels: string[]
):
  | 'asset'
  | 'risk'
  | 'seed'
  | 'technology'
  | 'webpage'
  | 'port'
  | 'adobject'
  | 'awsresource'
  | 'gcpresource'
  | 'azureresource'
  | 'cloudresource'
  | 'vulnerability'
  | undefined {
  const supported = labels
    .map(l => LABEL_TO_ENTITY[l.toLowerCase()])
    .filter(
      (
        e
      ): e is
        | 'asset'
        | 'risk'
        | 'seed'
        | 'technology'
        | 'webpage'
        | 'port'
        | 'adobject'
        | 'awsresource'
        | 'gcpresource'
        | 'azureresource'
        | 'cloudresource'
        | 'vulnerability' => !!e
    );
  const unique = [...new Set(supported)];
  return unique.length === 1 ? unique[0] : undefined;
}

/**
 * Entity-specific column configurations
 * Maps API entity type to column definitions
 */
const ENTITY_COLUMNS: Record<string, readonly ExportColumnConfig[]> = {
  asset: ASSET_COLUMNS,
  risk: VULNERABILITY_COLUMNS,
  seed: SEED_COLUMNS,
  technology: TECHNOLOGY_COLUMNS,
  webpage: WEBPAGE_COLUMNS,
  port: PORT_COLUMNS,
  adobject: AD_COLUMNS,
  awsresource: CLOUD_COLUMNS,
  gcpresource: CLOUD_COLUMNS,
  azureresource: CLOUD_COLUMNS,
  cloudresource: CLOUD_COLUMNS,
  vulnerability: VULNERABILITY_COLUMNS,
};

/**
 * Create query export configuration from SavedQuery
 *
 * @param savedQuery - Query from query builder
 * @returns Export config or null if entity type not supported
 *
 * @example
 * ```typescript
 * // Asset query
 * const assetQuery = { query: { node: { labels: ['Asset'], filters: [] } } };
 * const config = createQueryExportConfig(assetQuery);
 * // Returns config with asset columns
 *
 * // Port query (not supported)
 * const portQuery = { query: { node: { labels: ['Port'], filters: [] } } };
 * const config = createQueryExportConfig(portQuery);
 * // Returns null
 * ```
 */
export function createQueryExportConfig(
  savedQuery: SavedQuery
): UniversalExportConfig<ExportableEntity> | null {
  const labels = savedQuery.query?.node?.labels || [];
  const apiEntityType = getApiEntityType(labels);

  if (!apiEntityType) {
    return null; // Entity type not supported for export
  }

  const columns = ENTITY_COLUMNS[apiEntityType];
  if (!columns || columns.length === 0) {
    return null;
  }

  // Capture query reference for buildQuery closure
  const graphQuery = savedQuery.query;

  return {
    entityType: labels[0] || 'Query',
    apiEntityType,
    storageKey: `ADVANCE_EXPORT_OPTIONS.query.${apiEntityType}.v2`,
    filterSections: [], // No additional filters -- the query IS the filter
    columns: [...columns],
    formats: [
      { label: 'CSV', value: 'csv' },
      { label: 'JSON', value: 'json' },
      { label: 'PDF', value: 'pdf' },
    ],
    defaultFormat: { label: 'CSV', value: 'csv' },

    buildQuery: () => graphQuery,

    validateExport: (_filters, columns, _selectedRows) => {
      if (columns.length === 0) {
        return [
          {
            field: 'columns' as const,
            message: 'Please select at least one column to export.',
          },
        ];
      }
      return null;
    },
  };
}
